import React from "react";
import { ConversationComment } from "services/ConversationService";
import { UserWithFullName } from "components/Signals/Dashboard/DashboardMenu/ShareDialog/constants";
import { AcxMainTextFieldRef } from "components/UI/AcxMainTextField";
import CommentItem from "./CommentItem";
import ReplyComposer from "./ReplyComposer";
import EditComposerContainer from "./EditComposerContainer";
import MentionDropdown from "./MentionDropdown";
import useMentions from "./useMentions";
import { applyMentionSelection } from "./mentionUtils";

type CommentThreadProps = {
    comment: ConversationComment;
    replies: ConversationComment[];
    isChat: boolean;
    isReplying: boolean;
    isEditing: boolean;
    replyEditComment: ConversationComment | null;
    users: UserWithFullName[] | null | undefined;
    usersLoading: boolean;
    renderCommentContent: (
        comment: ConversationComment,
        options: { showTimestamp: boolean; editComposer?: React.ReactNode },
    ) => React.ReactNode;
    onAddReply: (
        parentComment: ConversationComment,
        commentText: string,
        taggedUserIds: string[],
    ) => Promise<void>;
    onSaveEdit: (
        commentId: string,
        commentText: string,
        taggedUserIds: string[],
    ) => Promise<void>;
    onCancelEdit: () => void;
    onReplyClick: (commentId: string) => void;
    onCancelReply: () => void;
    tokenizeMentions: (
        commentText: string,
        selections: UserWithFullName[],
    ) => string;
    detokenizeMentions: (commentText: string) => string;
    getTaggedUserIdsFromComment: (commentText: string) => string[];
};

const CommentThread = React.memo(
    ({
        comment,
        replies,
        isChat,
        isReplying,
        isEditing,
        replyEditComment,
        users,
        usersLoading,
        renderCommentContent,
        onAddReply,
        onSaveEdit,
        onCancelEdit,
        onReplyClick,
        onCancelReply,
        tokenizeMentions,
        detokenizeMentions,
        getTaggedUserIdsFromComment,
    }: CommentThreadProps) => {
        const [replyDraft, setReplyDraft] = React.useState("");
        const [isReplySaving, setIsReplySaving] = React.useState(false);
        const replyInputRef = React.useRef<AcxMainTextFieldRef>(null);
        const pendingReplyCursorPositionRef = React.useRef<number | null>(null);

        const {
            state: {
                matchingUsers: replyMatchingUsers,
                mentionSelections: replyMentionSelections,
                mentionStartIndex: replyMentionStartIndex,
                mentionDropdownPosition: replyMentionDropdownPosition,
                activeMentionIndex: replyActiveMentionIndex,
                shouldShowMentions: replyShouldShowMentions,
            },
            actions: {
                setMentionQuery: setReplyMentionQuery,
                setMentionStartIndex: setReplyMentionStartIndex,
                setMentionSelections: setReplyMentionSelections,
                setActiveMentionIndex: setReplyActiveMentionIndex,
                setCursorPosition: setReplyCursorPosition,
                syncCursorFromEvent: syncReplyCursorFromEvent,
                handleMentionKeyDown: handleReplyMentionKeyDown,
            },
            refs: { mentionDropdownRef: replyMentionDropdownRef },
        } = useMentions({
            users,
            usersLoading,
            inputRef: replyInputRef,
            inputValue: replyDraft,
        });

        const replyMentionListboxId = comment.id
            ? `reply-mention-listbox-${comment.id}`
            : "reply-mention-listbox";
        const getReplyMentionOptionId = React.useCallback(
            (user: UserWithFullName, index: number) =>
                `reply-mention-option-${comment.id ?? "root"}-${
                    user.userId ?? index
                }`,
            [comment.id],
        );

        const handleReplyClick = React.useCallback(
            (commentId: string) => {
                onReplyClick(commentId);
                setReplyDraft("");
                setReplyMentionSelections([]);
                setReplyMentionQuery("");
                setReplyMentionStartIndex(null);
            },
            [
                onReplyClick,
                setReplyDraft,
                setReplyMentionQuery,
                setReplyMentionSelections,
                setReplyMentionStartIndex,
            ],
        );

        const handleCancelReply = React.useCallback(() => {
            setReplyDraft("");
            setReplyMentionSelections([]);
            setReplyMentionQuery("");
            setReplyMentionStartIndex(null);
            onCancelReply();
        }, [
            onCancelReply,
            setReplyDraft,
            setReplyMentionQuery,
            setReplyMentionSelections,
            setReplyMentionStartIndex,
        ]);

        const handleReplyInputChange = React.useCallback(
            (
                event: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement
                >,
            ) => {
                setReplyDraft(event.target.value);
                syncReplyCursorFromEvent(event);
            },
            [setReplyDraft, syncReplyCursorFromEvent],
        );

        const handleReplyMentionBlur = React.useCallback(() => {
            setReplyMentionQuery("");
            setReplyMentionStartIndex(null);
        }, [setReplyMentionQuery, setReplyMentionStartIndex]);

        const handleSelectReplyMentionUser = React.useCallback(
            (user: UserWithFullName) => {
                applyMentionSelection(user, {
                    value: replyDraft,
                    mentionStartIndex: replyMentionStartIndex,
                    setValue: setReplyDraft,
                    setCursorPosition: setReplyCursorPosition,
                    pendingCursorPositionRef: pendingReplyCursorPositionRef,
                    setMentionQuery: setReplyMentionQuery,
                    setMentionStartIndex: setReplyMentionStartIndex,
                    setMentionSelections: setReplyMentionSelections,
                });
            },
            [
                replyDraft,
                replyMentionStartIndex,
                setReplyCursorPosition,
                setReplyMentionQuery,
                setReplyMentionSelections,
                setReplyMentionStartIndex,
            ],
        );

        const handleAddReply = React.useCallback(async () => {
            const trimmed = replyDraft.trim();
            if (!trimmed || isReplySaving) return;
            const tokenizedReply = tokenizeMentions(
                replyDraft,
                replyMentionSelections,
            );
            const resolvedTaggedUserIds =
                getTaggedUserIdsFromComment(tokenizedReply);

            setIsReplySaving(true);
            try {
                await onAddReply(
                    comment,
                    tokenizedReply.trim(),
                    resolvedTaggedUserIds,
                );
                setReplyDraft("");
                setReplyMentionSelections([]);
                setReplyMentionQuery("");
                setReplyMentionStartIndex(null);
                onCancelReply();
            } finally {
                setIsReplySaving(false);
            }
        }, [
            comment,
            getTaggedUserIdsFromComment,
            isReplySaving,
            onAddReply,
            onCancelReply,
            replyDraft,
            replyMentionSelections,
            setReplyMentionQuery,
            setReplyMentionSelections,
            setReplyMentionStartIndex,
            tokenizeMentions,
        ]);

        React.useEffect(() => {
            if (isReplying) return;
            setReplyDraft("");
            setReplyMentionSelections([]);
            setReplyMentionQuery("");
            setReplyMentionStartIndex(null);
        }, [
            isReplying,
            setReplyDraft,
            setReplyMentionQuery,
            setReplyMentionSelections,
            setReplyMentionStartIndex,
        ]);

        React.useEffect(() => {
            const pendingCursorPosition = pendingReplyCursorPositionRef.current;
            if (pendingCursorPosition === null) return;
            const timer = window.setTimeout(() => {
                const input = replyInputRef.current;
                if (!input) return;
                input.focus?.();
                if ("setSelectionRange" in input) {
                    input.setSelectionRange(
                        pendingCursorPosition,
                        pendingCursorPosition,
                    );
                }
                pendingReplyCursorPositionRef.current = null;
            }, 0);
            return () => window.clearTimeout(timer);
        }, [replyDraft]);

        React.useEffect(() => {
            if (!isReplying) return;
            const timer = window.setTimeout(() => {
                replyInputRef.current?.focus?.();
            }, 0);
            return () => window.clearTimeout(timer);
        }, [isReplying]);

        const replyMentionDropdown = (
            <MentionDropdown
                id={replyMentionListboxId}
                testId="reply-mention-dropdown"
                position={replyMentionDropdownPosition}
                isOpen={replyShouldShowMentions}
                users={replyMatchingUsers}
                activeIndex={replyActiveMentionIndex}
                dropdownRef={replyMentionDropdownRef}
                getOptionId={getReplyMentionOptionId}
                onHover={setReplyActiveMentionIndex}
                onSelect={handleSelectReplyMentionUser}
            />
        );

        const replyComposer = isReplying ? (
            <ReplyComposer
                mentionSelections={replyMentionSelections}
                value={replyDraft}
                isSaving={isReplySaving}
                inputRef={replyInputRef}
                onChange={handleReplyInputChange}
                inputProps={{
                    onClick: syncReplyCursorFromEvent,
                    onKeyUp: syncReplyCursorFromEvent,
                    onKeyDown: (event) =>
                        handleReplyMentionKeyDown(
                            event,
                            handleSelectReplyMentionUser,
                        ),
                    onBlur: handleReplyMentionBlur,
                    role: "combobox",
                    "aria-autocomplete": "list",
                    "aria-expanded": replyShouldShowMentions,
                    "aria-controls": replyShouldShowMentions
                        ? replyMentionListboxId
                        : undefined,
                    "aria-activedescendant": replyShouldShowMentions
                        ? getReplyMentionOptionId(
                              replyMatchingUsers[replyActiveMentionIndex],
                              replyActiveMentionIndex,
                          )
                        : undefined,
                }}
                onCancel={handleCancelReply}
                onSubmit={handleAddReply}
                mentionDropdown={replyMentionDropdown}
            />
        ) : null;

        const editComposer = isEditing ? (
            <EditComposerContainer
                comment={comment}
                isActive
                users={users}
                usersLoading={usersLoading}
                onCancelEdit={onCancelEdit}
                onSaveEdit={onSaveEdit}
                tokenizeMentions={tokenizeMentions}
                detokenizeMentions={detokenizeMentions}
                getTaggedUserIdsFromComment={getTaggedUserIdsFromComment}
            />
        ) : null;

        const replyEditComposer = replyEditComment ? (
            <EditComposerContainer
                comment={replyEditComment}
                isActive
                users={users}
                usersLoading={usersLoading}
                onCancelEdit={onCancelEdit}
                onSaveEdit={onSaveEdit}
                tokenizeMentions={tokenizeMentions}
                detokenizeMentions={detokenizeMentions}
                getTaggedUserIdsFromComment={getTaggedUserIdsFromComment}
            />
        ) : null;

        return (
            <CommentItem
                comment={comment}
                isChat={isChat}
                replies={replies}
                isReplying={isReplying}
                replyComposer={replyComposer}
                editComposer={editComposer}
                replyEditComposer={replyEditComposer}
                renderCommentContent={renderCommentContent}
                onReplyClick={handleReplyClick}
                replyEditCommentId={replyEditComment?.id ?? null}
            />
        );
    },
);

export default CommentThread;
